import { useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Sparkles, 
  TrendingUp, 
  AlertTriangle, 
  Target,
  DollarSign,
  Trophy,
  Zap,
  ChevronDown,
  ChevronUp,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AIRecommendations = ({ onSelectGame }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [preferences, setPreferences] = useState({
    budget: 50,
    risk_tolerance: 'medium',
    prize_goal: '500',  // Default to $500+ as a realistic target
    target_prize: 500,
    user_commentary: ''  // User's additional context for AI
  });

  const fetchRecommendations = async () => {
    setIsLoading(true);
    try {
      // Map prize_goal to target_prize for the API
      const targetPrize = preferences.prize_goal === 'any' ? 0 
        : preferences.prize_goal === 'jackpot' ? 100000 
        : parseInt(preferences.prize_goal) || 500;
      
      const response = await axios.post(`${API}/ai/recommendations`, {
        ...preferences,
        target_prize: targetPrize,
        user_commentary: preferences.user_commentary || null
      });
      setRecommendations(response.data);
      toast.success('AI analysis complete', {
        description: 'Strategic recommendations generated'
      });
    } catch (err) {
      console.error('Error fetching AI recommendations:', err);
      toast.error('Failed to generate recommendations', {
        description: err.response?.data?.detail || err.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRecommendationBadge = (rec) => {
    if (rec === 'STRONG BUY') return <Badge className="bg-success text-success-foreground">Strong Buy</Badge>;
    if (rec === 'BUY') return <Badge className="bg-accent text-accent-foreground">Buy</Badge>;
    if (rec === 'CONSIDER') return <Badge variant="outline">Consider</Badge>;
    return <Badge variant="secondary">{rec}</Badge>;
  };

  const getRiskIcon = (risk) => {
    if (risk === 'low') return <Target className="w-4 h-4 text-success" />;
    if (risk === 'medium') return <TrendingUp className="w-4 h-4 text-warning" />;
    return <Zap className="w-4 h-4 text-danger" />;
  };

  return (
    <Card className="border-accent/30 bg-gradient-to-br from-accent/5 to-transparent" data-testid="ai-recommendations">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            <span>AI Strategic Advisor</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            data-testid="ai-toggle-expand"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-4">
          {/* Preference Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="space-y-2">
              <label className="text-sm font-medium">Budget: ${preferences.budget}</label>
              <Slider
                value={[preferences.budget]}
                onValueChange={([value]) => setPreferences(prev => ({ ...prev, budget: value }))}
                min={10}
                max={200}
                step={10}
                className="w-full"
                data-testid="ai-budget-slider"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Risk Tolerance</label>
              <Select
                value={preferences.risk_tolerance}
                onValueChange={(value) => setPreferences(prev => ({ ...prev, risk_tolerance: value }))}
              >
                <SelectTrigger data-testid="ai-risk-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Consistent Returns</SelectItem>
                  <SelectItem value="medium">Medium - Balanced</SelectItem>
                  <SelectItem value="high">High - Big Wins</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Prize Goal</label>
              <Select
                value={preferences.prize_goal}
                onValueChange={(value) => setPreferences(prev => ({ ...prev, prize_goal: value }))}
              >
                <SelectTrigger data-testid="ai-goal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Win</SelectItem>
                  <SelectItem value="100">$100+ Prizes</SelectItem>
                  <SelectItem value="500">$500+ Prizes</SelectItem>
                  <SelectItem value="1000">$1,000+ Prizes</SelectItem>
                  <SelectItem value="5000">$5,000+ Prizes</SelectItem>
                  <SelectItem value="10000">$10,000+ Prizes</SelectItem>
                  <SelectItem value="50000">$50,000+ Prizes</SelectItem>
                  <SelectItem value="jackpot">Jackpot Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={fetchRecommendations}
                disabled={isLoading}
                className="w-full bg-accent hover:bg-accent/90"
                data-testid="ai-analyze-btn"
              >
                {isLoading ? (
                  <>Analyzing...</>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get AI Picks
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* User Commentary Input */}
          <div className="space-y-2 p-4 bg-muted/20 rounded-lg border border-border">
            <label className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-accent" />
              Additional Context for AI (Optional)
            </label>
            <Textarea
              placeholder="Tell the AI what you're looking for... e.g., 'I prefer games with multiple top prizes still available' or 'Focus on newer games released in the last 60 days' or 'I want to maximize my chances of winning at least $500'"
              value={preferences.user_commentary}
              onChange={(e) => setPreferences(prev => ({ ...prev, user_commentary: e.target.value }))}
              className="min-h-[80px] resize-none"
              data-testid="ai-commentary-input"
            />
            <p className="text-xs text-muted-foreground">
              Your input will be considered by the AI when generating personalized recommendations.
            </p>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {/* Results */}
          {recommendations && !isLoading && (
            <div className="space-y-4">
              {/* Top Picks */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-warning" />
                  Top Strategic Picks
                </h4>
                
                {recommendations.recommendations?.top_picks?.map((pick, idx) => (
                  <div 
                    key={pick.game_id || idx}
                    className="p-4 bg-card rounded-lg border border-border hover:border-accent/50 transition-colors cursor-pointer"
                    onClick={() => onSelectGame && onSelectGame(pick.game_id)}
                    data-testid={`ai-pick-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-accent">#{pick.rank}</span>
                        <div>
                          <div className="font-semibold">{pick.game_name}</div>
                          <div className="text-sm text-muted-foreground">${pick.price} ticket</div>
                        </div>
                      </div>
                      {getRecommendationBadge(pick.recommendation)}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">{pick.reasoning}</p>
                    
                    {/* Expected Outcome */}
                    {pick.expected_outcome && (
                      <div className="text-xs bg-muted/50 p-2 rounded mb-2">
                        <span className="font-medium text-foreground">Expected: </span>
                        <span className="text-muted-foreground">{pick.expected_outcome}</span>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Badge variant="outline" className="gap-1 bg-accent/10">
                        <DollarSign className="w-3 h-3" />
                        Allocate ${pick.allocation || Math.round(preferences.budget / 3)}
                      </Badge>
                      {pick.upper_tier_focus && (
                        <Badge variant="outline" className="gap-1">
                          <Target className="w-3 h-3" />
                          {pick.upper_tier_focus.substring(0, 50)}...
                        </Badge>
                      )}
                      {pick.time_efficiency && (
                        <Badge variant="outline" className="gap-1">
                          <Zap className="w-3 h-3" />
                          {pick.time_efficiency}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Budget Allocation Strategy */}
              {recommendations.recommendations?.strategy?.budget_allocation && (
                <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-accent" />
                    Budget Allocation Strategy
                  </h4>
                  {typeof recommendations.recommendations.strategy.budget_allocation === 'object' ? (
                    <div className="space-y-2">
                      {recommendations.recommendations.strategy.budget_allocation.plan?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{item.game_name}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">${item.spend}</Badge>
                            <span className="text-xs text-muted-foreground">({item.tickets} tickets)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {recommendations.recommendations.strategy.budget_allocation}
                    </p>
                  )}
                </div>
              )}

              {/* Upper-Tier Analysis */}
              {recommendations.recommendations?.upper_tier_analysis && (
                <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4 text-success" />
                    Upper-Tier Prize Opportunities ($100+)
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    {recommendations.recommendations.upper_tier_analysis.realistic_expectations}
                  </p>
                  {recommendations.recommendations.upper_tier_analysis.best_opportunities?.slice?.(0, 3)?.map?.((opp, idx) => (
                    <div key={idx} className="text-xs bg-muted/30 p-2 rounded mt-2">
                      <span className="font-medium">{opp.game_name}: </span>
                      <span className="text-muted-foreground">{opp.why}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Strategy Summary */}
              {recommendations.recommendations?.strategy?.prioritization && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-accent" />
                    Prioritization Strategy
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {recommendations.recommendations.strategy.prioritization}
                  </p>
                </div>
              )}

              {/* Warnings */}
              {recommendations.recommendations?.warnings?.length > 0 && (
                <div className="p-4 bg-danger/5 border border-danger/20 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-danger">
                    <AlertTriangle className="w-4 h-4" />
                    Warnings
                  </h4>
                  <ul className="text-sm space-y-1">
                    {recommendations.recommendations.warnings.map((warning, idx) => (
                      <li key={idx} className="text-muted-foreground">• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!recommendations && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Set your preferences and click "Get AI Picks" for personalized recommendations</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
